/**
 * Authenticated admin thumbnail with viewport-aware loading (ADR-025 B-4).
 *
 * The admin image route requires an Authorization header, so images are
 * fetched to object URLs. Near-viewport thumbnails acquire a shared,
 * reference-counted resource lease; the pool deduplicates identical
 * attachment+variant reads, caps global concurrency at eight, and revokes the
 * blob after the final consumer releases it. Full originals remain on-demand
 * only and are never silently replaced by the 256px thumbnail.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Box, Text } from '@bigcommerce/big-design';
import {
  acquireAdminImage,
  acquireExistingAdminImage,
  type AdminImageLease,
} from './adminImageResourcePool';
import { tokens } from '../../tokens';

export interface AdminImageThumbnailProps {
  /** Relative admin image URL (`/api/admin/images/att_…`). */
  src: string;
  alt: string;
  /** BigCommerce signed_payload_jwt for the authenticated fetch. */
  token: string;
  /** Receives a full-size object URL loaded on demand. */
  onClick?: (blobUrl: string) => void;
  /** Square thumbnail edge in px (default 120). */
  size?: number;
  /** Optional test id on the wrapper. */
  testId?: string;
  /**
   * Whether this consumer may own authenticated image resources.
   * Keep-alive panels pass false while hidden so their data state survives
   * without retaining object URLs (default true for standalone consumers).
   */
  active?: boolean;
}

function withThumbParam(src: string): string {
  return src.includes('?') ? `${src}&size=thumb` : `${src}?size=thumb`;
}

/** Mount image bytes only when the tile is in or near the viewport. */
function useNearViewport(): [React.RefObject<HTMLDivElement>, boolean] {
  const elementRef = useRef<HTMLDivElement>(null);
  const [nearViewport, setNearViewport] = useState(
    () => typeof IntersectionObserver === 'undefined'
  );

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setNearViewport(true);
      return undefined;
    }
    const element = elementRef.current;
    if (!element) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        setNearViewport(true);
        observer.disconnect();
      },
      { rootMargin: '320px 0px' }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [elementRef, nearViewport];
}

export function AdminImageThumbnail({
  src,
  alt,
  token,
  onClick,
  size = 120,
  testId,
  active = true,
}: AdminImageThumbnailProps): React.ReactElement {
  const [containerRef, nearViewport] = useNearViewport();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [focused, setFocused] = useState(false);
  const currentSrcRef = useRef<string | null>(src);
  const fullLeaseRef = useRef<{
    key: string;
    lease: AdminImageLease;
  } | null>(null);

  // A full-size lease belongs to exactly one token+src. Recycled table rows
  // release it before accepting another attachment. Section deactivation is
  // also a resource boundary: a hidden keep-alive panel must retain its data,
  // not authenticated object URLs.
  useEffect(() => {
    const previous = fullLeaseRef.current;
    if (previous) {
      previous.lease.release();
      fullLeaseRef.current = null;
    }
    currentSrcRef.current = active ? src : null;
    if (!active) return undefined;

    return () => {
      if (currentSrcRef.current === src) currentSrcRef.current = null;
      const current = fullLeaseRef.current;
      if (current) {
        current.lease.release();
        fullLeaseRef.current = null;
      }
    };
  }, [active, src, token]);

  useEffect(() => {
    setBlobUrl(null);
    setLoaded(false);
    setError(false);
    if (!active) return undefined;

    let live = true;
    const thumbSrc = withThumbParam(src);
    // A replacement BigDesign table cell starts with nearViewport=false in a
    // real browser because its first IntersectionObserver delivery is a later
    // task. Reuse only a resource the token-scoped pool already owns; never let
    // this path turn a genuinely new off-screen thumbnail into an eager read.
    const lease = nearViewport
      ? acquireAdminImage(token, thumbSrc)
      : acquireExistingAdminImage(token, thumbSrc);
    if (!lease) return undefined;
    void lease.url.then(
      (url) => {
        if (live) setBlobUrl(url);
      },
      () => {
        if (live) setError(true);
      }
    );
    return () => {
      live = false;
      lease.release();
    };
  }, [active, nearViewport, src, token]);

  const isInteractive = active && Boolean(onClick);

  const handleClick = async (): Promise<void> => {
    if (!active || !onClick || !blobUrl) return;

    const key = `${token}\u0000${src}`;
    let resource = fullLeaseRef.current;
    if (!resource || resource.key !== key) {
      resource?.lease.release();
      resource = { key, lease: acquireAdminImage(token, src) };
      fullLeaseRef.current = resource;
    }

    try {
      const url = await resource.lease.url;
      if (currentSrcRef.current === src && fullLeaseRef.current === resource) {
        onClick(url);
      }
    } catch {
      // A failed full-size fetch stays retryable and never degrades to the thumb.
      if (fullLeaseRef.current === resource) {
        resource.lease.release();
        fullLeaseRef.current = null;
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    void handleClick();
  };

  return (
    <div
      ref={containerRef}
      {...(isInteractive
        ? {
            role: 'button',
            tabIndex: 0,
            'aria-label': `View full-size image: ${alt}`,
            onClick: () => void handleClick(),
            onKeyDown: handleKeyDown,
            onFocus: () => setFocused(true),
            onBlur: () => setFocused(false),
          }
        : {})}
      data-testid={testId}
      data-admin-image-src={src}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: tokens.dimension.radius.lg,
        overflow: 'hidden',
        cursor: active && blobUrl && onClick ? 'pointer' : 'default',
        backgroundColor: tokens.color.surface.subtle,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: `1px solid ${tokens.color.border.default}`,
        boxShadow: focused ? `0 0 0 2px ${tokens.color.interactive.focusRingInner}, 0 0 0 4px ${tokens.color.interactive.focusRing}` : undefined,
        minWidth: '44px',
        minHeight: '44px',
      }}
    >
      {active && !loaded && !error && (
        <Box
          backgroundColor="secondary20"
          aria-hidden="true"
          style={{
            width: '100%',
            height: '100%',
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
      )}
      {active && error && (
        <Text color="secondary">
          Failed to load
        </Text>
      )}
      {active && blobUrl && (
        <img
          src={blobUrl}
          alt={alt}
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: loaded && !error ? 'block' : 'none',
          }}
        />
      )}
    </div>
  );
}
